/* eslint-disable react/no-array-index-key */

import Checkbox from "@material-ui/core/Checkbox";
import InputLabel from "@material-ui/core/InputLabel";
import ListSubheader from "@material-ui/core/ListSubheader";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import FormControl, { FormControlProps } from "@material-ui/core/FormControl";
import ListItemText, { ListItemTextProps } from "@material-ui/core/ListItemText";
import MUISelect, { SelectProps as MUISelectProps } from "@material-ui/core/Select";
import React, { FC, useState } from "react";
import { Box, CircularProgress } from "@material-ui/core";
import { FixedSizeList as List } from "react-window";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

export type SelectOptionType = {
  label: string;
  value: string;
  key?: string | number;
};

type SelectWithCheckboxesRedesignProps = {
  options: SelectOptionType[];
};

const MENU_MAX_HEIGHT = 320;
const SEARCH_HEADER_HEIGHT = 56;
const ITEM_HEIGHT = 44;
const LIST_MAX_HEIGHT = MENU_MAX_HEIGHT - SEARCH_HEADER_HEIGHT;

const CoralCheckbox = styled(Checkbox)(({ theme }: { theme: Theme }) => ({
  "&.Mui-checked": {
    color: theme.palette.brand.coral,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }: { theme: Theme }) => ({
  "& .MuiInputBase-root": {
    height: 40,
    fontSize: 13,
    color: theme.palette.brand.gray900,
    background: theme.palette.brand.white,
    borderRadius: 8,
  },
  "& .MuiSelect-select": {
    padding: "10px 32px 10px 14px",
    minHeight: "unset",
    fontWeight: 500,
  },
  "& .MuiInputLabel-outlined": {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.brand.gray500,
    transform: "translate(14px, 11px) scale(1)",
  },
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: "translate(14px, -6px) scale(0.85)",
    background: theme.palette.brand.white,
    padding: "0 4px",
  },
  "& label.Mui-focused": {
    color: theme.palette.brand.gray500,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    "& fieldset": {
      borderColor: theme.palette.brand.gray200,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.brand.gray300,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.brand.blueAccent,
      borderWidth: 1,
    },
  },
  "& .MuiSelect-icon": {
    color: theme.palette.brand.gray700,
    right: 10,
  },
}));

const StyledListItemText = styled(ListItemText)<ListItemTextProps>(() => ({
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
}));

const SearchHeader = styled(ListSubheader)(({ theme }: { theme: Theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: theme.palette.brand.white,
  padding: "8px 12px",
  lineHeight: "normal",
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const SearchField = styled(TextField)(({ theme }: { theme: Theme }) => ({
  width: "100%",
  "& .MuiInputBase-root": {
    height: 36,
    fontSize: 13,
    borderRadius: 8,
    background: theme.palette.brand.white,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    "& fieldset": {
      borderColor: theme.palette.brand.gray200,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.brand.gray300,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.brand.blueAccent,
      borderWidth: 1,
    },
  },
}));

const OptionRow = styled(MenuItem)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray900,
  minHeight: ITEM_HEIGHT,
  paddingTop: 6,
  paddingBottom: 6,
  "&:hover": {
    background: theme.palette.brand.gray50,
  },
}));

const EmptyState = styled(MenuItem)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
  justifyContent: "center",
}));

const getListHeight = (itemCount: number): number => {
  if (itemCount === 0) return ITEM_HEIGHT;
  return Math.min(itemCount * ITEM_HEIGHT, LIST_MAX_HEIGHT);
};

const SelectWithCheckboxesRedesign: FC<MUISelectProps & FormControlProps & SelectWithCheckboxesRedesignProps> = ({
  variant,
  className,
  label,
  value: propValue,
  onChange: propOnChange,
  name,
  options,
  ...rest
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>((propValue as string[]) || []);
  const [searchTerm, setSearchTerm] = useState("");

  const sortedOptions = [...options].sort((a, b) => a.label.localeCompare(b.label));
  const filteredOptions = sortedOptions.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const listHeight = getListHeight(filteredOptions.length);

  const handleChange = (value: string) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];

    setSelectedValues(newSelectedValues);

    if (propOnChange) {
      const event = {
        target: { value: newSelectedValues, name },
      } as unknown as React.ChangeEvent<{ value: unknown }>;

      propOnChange(event, null);
    }
  };

  const handleMenuClose = () => {
    setSearchTerm("");
  };

  const hasValue = selectedValues.length > 0;

  return (
    <StyledFormControl variant={variant ?? "outlined"} className={className} fullWidth>
      <InputLabel {...(hasValue ? { shrink: true } : {})}>{label}</InputLabel>
      <MUISelect
        value={selectedValues}
        displayEmpty
        renderValue={(selected) => {
          const selectedLabels = options.filter((x) => (selected as string[]).includes(x.value)).map((x) => x.label);

          return selectedLabels.length > 0 ? selectedLabels.join(", ") : "";
        }}
        name={name}
        label={label}
        onClose={handleMenuClose}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          getContentAnchorEl: null,
          PaperProps: {
            style: {
              borderRadius: 8,
              marginTop: 4,
              maxHeight: MENU_MAX_HEIGHT,
              overflow: "hidden",
              boxShadow: "0 8px 24px rgba(15, 43, 76, 0.12)",
            },
          },
          MenuListProps: {
            style: {
              paddingTop: 0,
              paddingBottom: 0,
              maxHeight: MENU_MAX_HEIGHT,
              overflow: "hidden",
            },
          },
        }}
        multiple
        {...rest}
      >
        {!options || options.length === 0 ? (
          <Box display="flex" alignItems="center" justifyContent="center" height={120}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <SearchHeader disableSticky={false}>
              <SearchField
                fullWidth
                variant="outlined"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </SearchHeader>

            {filteredOptions.length === 0 ? (
              <EmptyState disabled>No results found</EmptyState>
            ) : (
              <List height={listHeight} itemCount={filteredOptions.length} itemSize={ITEM_HEIGHT} width="100%">
                {({ index, style }: any) => {
                  const option = filteredOptions[index];
                  return (
                    <OptionRow
                      key={option.key || option.label}
                      value={option.value}
                      style={style}
                      onClick={() => handleChange(option.value)}
                    >
                      <CoralCheckbox checked={selectedValues.includes(option.value)} size="small" />
                      <StyledListItemText primary={option.label} />
                    </OptionRow>
                  );
                }}
              </List>
            )}
          </>
        )}
      </MUISelect>
    </StyledFormControl>
  );
};

export default SelectWithCheckboxesRedesign;
